import type { TFunction } from 'i18next';

import type { VisitQa } from '../types/api';
import { humanizeKey } from './reportFields';

export type QaCheckStatus = 'failed' | 'unmeasured' | 'passed' | 'informational';

export type QaCheckRow = {
    key: string;
    status: QaCheckStatus;
    reason?: string;
};

/** Display name for a check; one this app doesn't know yet is humanized from its key. */
export function qaCheckName(t: TFunction, key: string): string {
    return t(`features.visits.qa.checks.${key}`, { defaultValue: humanizeKey(key) });
}

/**
 * Checks a retry can fix: the officer stood in the wrong place or left too
 * early. Anything else (photo reuse, device clock, missing photo GPS,
 * clustering, a coordinate outside the boundary, or a check this app doesn't
 * know yet) points at the officer or the device, so a retry would only repeat
 * it — those are shown as a warning instead.
 */
export const RETRY_APPROPRIATE_CHECKS: readonly string[] = [
    'photo_coordinate_spread',
    'photos_outside_session',
    'time_on_site_short',
];

export const isRetryAppropriate = (key: string) => RETRY_APPROPRIATE_CHECKS.includes(key);

/** Officer-facing instructions for a retry visit, one line per reason. */
export function retryInstructions(t: TFunction, checkKeys: string[], other: string): string {
    const lines = checkKeys
        .filter(isRetryAppropriate)
        .map((key) => t(`features.visits.retry.instructions.${key}`));
    if (other.trim()) lines.push(other.trim());
    if (!lines.length) return '';
    return [t('features.visits.retry.instructionsIntro'), ...lines.map((l) => `• ${l}`)].join('\n');
}

export type QaVerdict = 'checks_passed' | 'needs_attention' | 'failed' | 'not_run';

// Failures lead, then what couldn't be measured, then passes, then the checks
// that only ever inform.
const ORDER: Record<QaCheckStatus, number> = {
    failed: 0,
    unmeasured: 1,
    passed: 2,
    informational: 3,
};

/**
 * Every automated check on a visit, worst first, plus the overall verdict.
 * A check fails when it is flagged or listed in `firedChecks` — unless it is
 * informational, which never counts against the visit even when it fires.
 */
export function readVisitQa(qa: VisitQa | null | undefined): { verdict: QaVerdict; checks: QaCheckRow[] } {
    const fired = new Set(qa?.firedChecks ?? []);
    const checks = Object.entries(qa?.checks ?? {})
        .filter(([, c]) => Boolean(c) && typeof c === 'object')
        .map(([key, c]): QaCheckRow => {
            let status: QaCheckStatus;
            if (c.informational === true) status = 'informational';
            else if (c.flagged === true || fired.has(key)) status = 'failed';
            else status = c.value === null || c.value === undefined ? 'unmeasured' : 'passed';
            const reason = typeof c.reason === 'string' && c.reason.trim() ? c.reason.trim() : undefined;
            return { key, status, reason };
        })
        .sort((a, b) => ORDER[a.status] - ORDER[b.status]);

    const result = qa?.result;
    const verdict: QaVerdict =
        result === 'checks_passed' || result === 'needs_attention' || result === 'failed'
            ? result
            : checks.length
              ? checks.some((c) => c.status === 'failed')
                  ? 'needs_attention'
                  : 'checks_passed'
              : 'not_run';
    return { verdict, checks };
}
